import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { LanguageSelector } from "../components/LanguageSelector";
import { ReviewsSection } from "../components/Reviews";
import { InteractiveDemo } from "../components/InteractiveDemo";
import { 
  Mic, 
  FileText, 
  BarChart3, 
  CreditCard, 
  Users, 
  Zap,
  ArrowRight,
  CheckCircle2,
  Play,
  Video,
  Brain,
  Shield,
  Sparkles,
  Clock,
  TrendingUp,
  Globe,
  Award,
  Target,
  Gift
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.user_id) {
            setIsLoggedIn(true);
          }
        }
      } catch (error) {}
    };
    checkAuth();
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
          <img 
            src={require('../assets/ema-logo.png')} 
            alt="Ema" 
            className="h-16 sm:h-24 md:h-32 w-auto"
          />
          
          <div className="hidden md:flex items-center gap-8">
            <a 
              href="#features" 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-slate-600 hover:text-blue-900 text-sm font-semibold transition-colors cursor-pointer"
            >
              {t('landing.featuresNav', 'Fonctionnalités')}
            </a>
            <a 
              href="#how-it-works" 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-slate-600 hover:text-blue-900 text-sm font-semibold transition-colors cursor-pointer"
            >
              {t('landing.howItWorks', 'Comment ça marche')}
            </a>
            <a 
              href="#demo" 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-slate-600 hover:text-blue-900 text-sm font-semibold transition-colors cursor-pointer"
            >
              {t('landing.demo', 'Démo')}
            </a>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSelector />
            {isLoggedIn ? (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-blue-900 hover:bg-blue-800 text-white text-xs sm:text-sm px-3 sm:px-4"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost"
                  onClick={handleLogin}
                  className="text-slate-600 hover:text-blue-900 hidden sm:inline-flex"
                >
                  {t('landing.login', 'Connexion')}
                </Button>
                <Button 
                  onClick={handleLogin}
                  className="bg-gradient-to-r from-blue-900 to-cyan-600 hover:from-blue-800 hover:to-cyan-500 text-white shadow-lg shadow-blue-900/25 text-xs sm:text-sm px-3 sm:px-4"
                >
                  {t('landing.freeTrial', 'Essai gratuit')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-cyan-50 via-blue-50 to-transparent -z-10" />
        <div className="absolute top-40 right-20 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-900/5 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-6 bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100">
                <Sparkles className="w-3 h-3 mr-1" />
                {t('landing.tagline')}
              </Badge>
              
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-4 sm:mb-6 tracking-tight" style={{ wordSpacing: '0.1em' }}>
                {t('landing.title')}
                <span className="block bg-gradient-to-r from-blue-900 via-blue-700 to-cyan-600 bg-clip-text text-transparent tracking-normal" style={{ wordSpacing: '0.15em' }}>
                  {t('landing.titleHighlight')}
                </span>
              </h1>
              
              <p className="text-base sm:text-xl text-slate-600 mb-6 sm:mb-8 leading-relaxed">
                {t('landing.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Button 
                  size="lg"
                  onClick={handleLogin}
                  className="bg-gradient-to-r from-blue-900 to-cyan-600 hover:from-blue-800 hover:to-cyan-500 text-white shadow-xl shadow-blue-900/25 h-14 px-8 text-lg"
                >
                  {t('landing.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 h-14 px-8 text-lg"
                >
                  <Play className="w-5 h-5 mr-2" />
                  {t('landing.seeDemo')}
                </Button>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                  {t('landing.freeInterviews')}
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                  {t('landing.noCardRequired')}
                </div>
              </div>
            </div>
            
            {/* Hero Visual */}
            <div className="relative hidden sm:block">
              <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-8 shadow-2xl">
                <div className="bg-slate-800/50 rounded-2xl p-6 mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{t('landing.hero.aiAssistant')}</p>
                      <p className="text-slate-400 text-sm">{t('landing.hero.interviewInProgress')}</p>
                    </div>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    "{t('landing.hero.sampleQuestion')}"
                  </p>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse" />
                    <span className="text-cyan-400 text-sm">{t('landing.hero.realTimeAnalysis')}</span>
                  </div>
                  <div className="bg-slate-800 rounded-lg px-3 py-2">
                    <p className="text-xs text-slate-400">{t('landing.hero.currentScore')}</p>
                    <p className="text-xl font-bold text-cyan-400">87/100</p>
                  </div>
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl p-4 border border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t('reports.recommendation')}</p>
                    <p className="text-sm font-semibold text-green-600">{t('dashboard.go')}</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl p-4 border border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t('landing.avgDuration')}</p>
                    <p className="text-sm font-semibold text-slate-900">25 min</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-blue-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { value: "3×", labelKey: "stats.faster" },
              { value: "0%", labelKey: "stats.biasReduction" },
              { value: "24/7", labelKey: "stats.availability" },
              { value: "Beta", labelKey: "stats.companies" }
            ].map((stat, i) => {
              const labels = {
                "stats.faster": { fr: "Plus rapide qu'un entretien classique", en: "Faster than a classic interview", es: "Más rápido que una entrevista clásica" },
                "stats.biasReduction": { fr: "Évaluation sans biais humains", en: "Bias-free evaluation", es: "Evaluación sin sesgos" },
                "stats.availability": { fr: "Disponibilité candidats", en: "Candidate availability", es: "Disponibilidad de candidatos" },
                "stats.companies": { fr: "Version bêta • Rejoignez-nous", en: "Beta version • Join us", es: "Versión beta • Únete" }
              };
              const icons = [Zap, Target, Clock, Users];
              const Icon = icons[i];
              const lang = t('common.language') === 'Langue' ? 'fr' : (t('common.language') === 'Idioma' ? 'es' : 'en');
              return (
                <div key={i} className="text-center" data-testid={`landing-stat-${i}`}>
                  <Icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                  <p className="text-2xl sm:text-4xl font-bold text-white mb-1 tracking-wide">{stat.value}</p>
                  <p className="text-slate-400 text-xs sm:text-sm px-2">{labels[stat.labelKey][lang] || labels[stat.labelKey].en}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200">
              {t('landing.featuresNav')}
            </Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight" style={{ wordSpacing: "0.12em" }}>
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Mic,
                titleKey: "features.aiInterview",
                descKey: "features.aiInterviewDesc",
                color: "cyan"
              },
              {
                icon: FileText,
                titleKey: "features.smartAnalysis",
                descKey: "features.smartAnalysisDesc",
                color: "blue"
              },
              {
                icon: BarChart3,
                titleKey: "features.detailedReports",
                descKey: "features.detailedReportsDesc",
                color: "indigo"
              },
              {
                icon: Video,
                titleKey: "features.videoCapture",
                descKey: "features.videoCaptureDesc",
                color: "purple"
              }
            ].map((feature, i) => (
              <Card key={i} className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-${feature.color}-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className={`w-6 h-6 text-${feature.color}-600`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 tracking-wide">{t(`landing.${feature.titleKey}`)}</h3>
                  <p className="text-slate-600 text-sm">{t(`landing.${feature.descKey}`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works Section */}
      <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">
              {t('landing.howItWorks')}
            </Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight" style={{ wordSpacing: "0.12em" }}>
              {t('landing.howItWorksSection.title')}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t('landing.howItWorksSection.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                titleKey: "howItWorksSection.step1",
                descKey: "howItWorksSection.step1Desc",
                icon: FileText
              },
              {
                step: "02",
                titleKey: "howItWorksSection.step2",
                descKey: "howItWorksSection.step2Desc",
                icon: Users
              },
              {
                step: "03",
                titleKey: "howItWorksSection.step3",
                descKey: "howItWorksSection.step3Desc",
                icon: BarChart3
              }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                  <span className="text-6xl font-bold text-slate-100 absolute top-4 right-4">{item.step}</span>
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-900 to-cyan-600 flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3 tracking-wide">{t(`landing.${item.titleKey}`)}</h3>
                  <p className="text-slate-600">{t(`landing.${item.descKey}`)}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <InteractiveDemo />

      {/* Product Suite - This section uses specific product names, keeping them as is */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200">
              {t('landing.product')}
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight" style={{ wordSpacing: "0.12em" }}>
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Mic, 
                titleKey: "features.aiInterview",
                descKey: "features.aiInterviewDesc",
                tag: "Core",
                tagColor: "cyan"
              },
              { 
                icon: Video, 
                titleKey: "features.videoCapture",
                descKey: "features.videoCaptureDesc",
                tag: "Premium",
                tagColor: "purple"
              },
              { 
                icon: Brain, 
                titleKey: "features.smartAnalysis",
                descKey: "features.smartAnalysisDesc",
                tag: "Core",
                tagColor: "cyan"
              },
              { 
                icon: Shield, 
                titleKey: "features.integrations",
                descKey: "features.integrationsDesc",
                tag: "Soon",
                tagColor: "slate"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center group-hover:bg-cyan-50 transition-colors">
                    <item.icon className="w-6 h-6 text-blue-900" />
                  </div>
                  <Badge variant="outline" className={`text-xs border-${item.tagColor}-200 text-${item.tagColor}-600 bg-${item.tagColor}-50`}>
                    {item.tag}
                  </Badge>
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2 tracking-wide">{t(`landing.${item.titleKey}`)}</h3>
                <p className="text-sm text-slate-600">{t(`landing.${item.descKey}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection limit={6} />

      {/* Free Trial Section */}
      <section id="free-trial" className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-green-100 text-green-700 border-green-200">
            <Gift className="w-3 h-3 mr-1" />
            {t('landing.freeOffer')}
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight" style={{ wordSpacing: "0.12em" }}>
            {t('landing.freeSection.title')}
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            {t('landing.freeSection.subtitle')}
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: CheckCircle2, textKey: 'freeSection.feature1' },
              { icon: Clock, textKey: 'freeSection.feature2' },
              { icon: Target, textKey: 'freeSection.feature3' }
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                <item.icon className="w-5 h-5 text-green-600" />
                <span className="text-slate-700 font-medium">{t(`landing.${item.textKey}`)}</span>
              </div>
            ))}
          </div>
          
          <Button 
            size="lg"
            onClick={handleLogin}
            className="bg-gradient-to-r from-blue-900 to-cyan-600 hover:from-blue-800 hover:to-cyan-500 text-white h-14 px-12 text-lg font-semibold shadow-xl shadow-blue-900/25"
            data-testid="start-free-trial-btn"
          >
            {t('landing.freeSection.cta')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight" style={{ wordSpacing: "0.12em" }}>
            {t('landing.testimonials.title')}
          </h2>
          <p className="text-xl text-blue-200 mb-10">
            {t('landing.recruiters')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={handleLogin}
              className="bg-white text-blue-900 hover:bg-blue-50 h-14 px-10 text-lg font-semibold shadow-xl"
            >
              {t('landing.cta')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-white/30 text-white hover:bg-white h-14 px-10 text-lg"
            >
              <Play className="w-5 h-5 mr-2" />
              {t('landing.seeDemo')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-12">
            <div>
              <img 
                src={require('../assets/ema-logo.png')} 
                alt="Ema" 
                className="h-12 w-auto mb-4 brightness-0 invert opacity-80"
              />
              <p className="text-sm">
                {t('landing.footer.description')}
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 tracking-wide">{t('landing.footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-cyan-400 transition-colors">{t('landing.footer.features')}</a></li>
                <li><a href="#free-trial" className="hover:text-cyan-400 transition-colors">{t('landing.freeTrial')}</a></li>
                <li><a href="#demo" className="hover:text-cyan-400 transition-colors">{t('landing.demo')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 tracking-wide">{t('landing.footer.company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-cyan-400 transition-colors">{t('landing.footer.about')}</a></li>
                <li>
                  <a href="mailto:contact@emaai.tech" className="hover:text-cyan-400 transition-colors flex items-center gap-2">
                    <span>{t('landing.footer.contact')}</span>
                  </a>
                  <span className="text-xs text-slate-500 mt-1 block">contact@emaai.tech</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 tracking-wide">{t('landing.footer.legal')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="hover:text-cyan-400 transition-colors">{t('landing.footer.terms')}</Link></li>
                <li><Link to="/privacy" className="hover:text-cyan-400 transition-colors">{t('landing.footer.privacy')}</Link></li>
                <li><Link to="/privacy#rgpd" className="hover:text-cyan-400 transition-colors">{t('landing.footer.gdpr')}</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">
              {t('landing.footer.copyright')}
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Globe className="w-4 h-4" />
              <span>{t('landing.footer.gdpr')}</span>
              <span className="mx-2">•</span>
              <Shield className="w-4 h-4" />
              <span>{t('common.status')}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
